import * as LPN from 'libphonenumber-js';
import { tryCatch } from '../error/error';

const DEFAULT_PHONE_NUMBER_COUNTRY: LPN.CountryCode = 'US';
const internationalPhoneNumberPrefixRegex = /^(?:\+|00)/;

const normalizePhoneNumberInput = (input: string) => {
  return input.trim().replace(/^00(?=\d)/, '+');
};

const parseValidPhoneNumber = (input: string): MaybeUndefined<LPN.PhoneNumber> => {
  const normalizedInput = normalizePhoneNumberInput(input);

  if (!normalizedInput) {
    return undefined;
  }

  const explicitPhoneNumber = LPN.parsePhoneNumberFromString(normalizedInput);
  if (explicitPhoneNumber?.isValid()) {
    return explicitPhoneNumber;
  }

  if (internationalPhoneNumberPrefixRegex.test(normalizedInput)) {
    return undefined;
  }

  const defaultCountryPhoneNumber = LPN.parsePhoneNumberFromString(normalizedInput, DEFAULT_PHONE_NUMBER_COUNTRY);
  if (!defaultCountryPhoneNumber?.isValid()) {
    return undefined;
  }

  return defaultCountryPhoneNumber;
};

const formatPhoneNumberForDisplay = (phoneNumber: LPN.PhoneNumber): string => {
  const internationalFormat = phoneNumber.formatInternational();

  return internationalFormat.replace(/^(\+1) (\d{3}) (\d{3}) (\d{4})$/, '$1 $2-$3-$4');
};

export const findPhoneNumbersInText = (
  text: string,
  options?:
    | LPN.CountryCode
    | {
        defaultCallingCode?: string;
        defaultCountry?: LPN.CountryCode;
        extended?: boolean;
      },
): LPN.NumberFound[] => {
  return LPN.findPhoneNumbersInText(text, options);
};

export const stringToE164 = tryCatch((input: string) => {
  return parseValidPhoneNumber(input)?.format('E.164');
});

export const stringToInternational = tryCatch((input: string) => {
  const phoneNumber = parseValidPhoneNumber(input);
  if (!phoneNumber) {
    return undefined;
  }

  return formatPhoneNumberForDisplay(phoneNumber);
});

// Example input: +16505802564, example output: +1 650-580-2564
export const e164ToInternational = stringToInternational;
